import {noop} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/floating-ui/Tooltip';

import EditButtons, {EditButtonStates} from './EditButtons';

export interface MetadataTableHeaderBaseProps {
  title: React.ReactNode;
  /**
   * For readonly content, use {@link MetadataTableHeaderReadonlyContent}
   */
  headerContent: React.ReactNode;
  customButton?: JSX.Element | null;
  isEditable: boolean; // Are any edits allowed?
  isEditing?: boolean;
  disableEditWithTooltip?: string | null;
  onEdit?: () => void;
  handleCancelEdit?: () => void;
  handleCompleteEdit?: () => void;
}

export function MetadataTableHeaderReadonlyContent(props: {text: string}): JSX.Element {
  return <div className="faded">{props.text}</div>;
}

export default function MetadataTableHeader(props: MetadataTableHeaderBaseProps): JSX.Element {
  const renderTitleAndContent = (): JSX.Element => (
    <div className="metadata-header-cell simpleDivTable-headerCell">
      <div className="u-small-spacing-bottom">
        <span className="metadata-table-header">{props.title}</span>
        {props.headerContent}
      </div>
    </div>
  );

  const validateEditButtonProps = (): void => {
    if (props.isEditable) {
      assert(props.customButton == null, 'Cannot specify custom header button and use EditButtons');
      assert(props.onEdit != null, 'MetadataTableHeader: onEdit must be provided when isEditable is true');
      assert(
        props.handleCancelEdit != null,
        'MetadataTableHeader: handleCancelEdit must be provided when isEditable is true'
      );
      assert(
        props.handleCompleteEdit != null,
        'MetadataTableHeader: handleCompleteEdit must be provided when isEditable is true'
      );
    }
  };

  const renderHeaderButtons = (): JSX.Element => {
    validateEditButtonProps();
    const displayEditButton = props.isEditable || props.disableEditWithTooltip != null;
    return (
      <div className="metadata-header-cell simpleDivTable-headerCell">
        <div className="u-text-align-right">
          {displayEditButton && (
            <Tooltip text={props.disableEditWithTooltip} isDisabled={props.disableEditWithTooltip == null}>
              <EditButtons
                editingState={props.isEditing ? EditButtonStates.EDITING : EditButtonStates.NOT_EDITING}
                editDisabled={props.disableEditWithTooltip != null}
                onCancel={props.handleCancelEdit || noop}
                onComplete={props.handleCompleteEdit || noop}
                onEdit={props.onEdit || noop}
              />
            </Tooltip>
          )}
          {props.customButton != null && props.customButton}
        </div>
      </div>
    );
  };

  return (
    <div
      className="simpleDivTable-header"
      {...getTestProps({component: 'MetadataTableHeader', element: 'root'})}
    >
      {renderTitleAndContent()}
      {renderHeaderButtons()}
    </div>
  );
}
